import React from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Checkbox,
  Typography,
  Box
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { useTranslations } from 'next-intl';
import { type SubTask, type Task } from '../../../../stores/TaskStore';
import CustomMuiScrollBar from '@/components/ui/ScrollBar';

interface SubTaskListProps {
  task: Task;
  editingSubTaskId: string | null;
  editingSubTaskContent: string;
  setEditingSubTaskContent: (content: string) => void;
  handleToggleSubTask: (subTaskId: string) => void;
  handleDeleteSubTask: (subTaskId: string) => void;
  handleEditSubTask: (subTask: SubTask) => void;
  handleUpdateSubTaskContent: () => void;
}

export const SubTaskList: React.FC<SubTaskListProps> = ({
  task,
  editingSubTaskId,
  editingSubTaskContent,
  setEditingSubTaskContent,
  handleToggleSubTask,
  handleDeleteSubTask,
  handleEditSubTask,
  handleUpdateSubTaskContent
}) => {
  const t = useTranslations('TaskManager');

  return (
    <Box sx={{
      flex: 1,
      overflowY: 'auto',
      ...CustomMuiScrollBar()
    }} className="custom-scrollbar">
      {task.subTasks && task.subTasks.length > 0 ? (
        <List sx={{ p: 0 }} dense={true}>
          {task.subTasks.map((subTask) => (
            <ListItem
              key={subTask.id}
              sx={{
                borderRadius: 16,
                mb: 1,
                backgroundColor: subTask.completed
                  ? 'var(--color-secondary)'
                  : 'var(--color-muted)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: subTask.completed
                    ? 'var(--color-accent)'
                    : 'var(--color-primary-light)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  checked={subTask.completed}
                  onChange={() => handleToggleSubTask(subTask.id)}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  sx={{
                    color: 'var(--color-primary)',
                    '&.Mui-checked': {
                      color: 'var(--color-success)'
                    }
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  editingSubTaskId === subTask.id ? (
                    <TextField
                      fullWidth
                      variant="standard"
                      value={editingSubTaskContent}
                      onChange={(e) => setEditingSubTaskContent(e.target.value)}
                      onBlur={handleUpdateSubTaskContent}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdateSubTaskContent();
                        }
                      }}
                      autoFocus
                      sx={{
                        '& .MuiInput-underline:before': { borderBottom: 'none' },
                        '& .MuiInput-underline:after': { borderBottom: 'none' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                        '& .MuiInputBase-input': {
                          padding: 0,
                          fontWeight: 500,
                          color: 'var(--color-foreground)',
                        }
                      }}
                    />
                  ) : (
                    <Typography
                      onClick={() => handleEditSubTask(subTask)}
                      sx={{
                        cursor: 'pointer',
                        textDecoration: subTask.completed ? 'line-through' : 'none',
                        color: subTask.completed
                          ? 'var(--color-muted-foreground)'
                          : 'var(--color-foreground)',
                        fontWeight: subTask.completed ? 400 : 500,
                        wordBreak: 'break-word'
                      }}
                    >
                      {subTask.content}
                    </Typography>
                  )
                }
              />
              <IconButton
                edge="end"
                onClick={() => handleDeleteSubTask(subTask.id)}
                size="small"
                sx={{
                  color: 'var(--color-button-delete)',
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'var(--color-button-delete)' + '15'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--color-muted-foreground)',
            textAlign: 'center'
          }}
        >
          <CheckCircleIcon sx={{ fontSize: '3rem', opacity: 0.3, mb: 1 }} />
          <Typography variant="body2">
            {t('SubTasks.noSubTasks')}
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            {t('SubTasks.clickToAddSubTasks')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};